import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spin, Empty, message, Select } from 'antd'
import { analyticsSession, resetSessionIfLeavingAnalytics } from 'utils/analyticsSession'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts'
import candidateRequest from 'utils/candidateRequest'

// ── Palette ───────────────────────────────────────────────────────────────────
const BRAND   = '#3249D7'
const PALETTE = ['#3249D7','#06B6D4','#8B5CF6','#F59E0B','#10B981','#EF4444','#EC4899','#F97316','#14B8A6','#6366F1']

const PIE_COLORS = {
  Male:                   '#3249D7',
  Female:                 '#EC4899',
  Transgender:            '#8B5CF6',
  Other:                  '#06B6D4',
  Intersex:               '#F97316',
  'Non Binary':           '#14B8A6',
  'Prefer Not To Say':    '#94a3b8',
  Unknown:                '#cbd5e1',
  'Passed Out':           '#10B981',
  Active:                 '#3249D7',
  Experienced:            '#F59E0B',
  Fresher:                '#3249D7',
}

// ── KPI config ────────────────────────────────────────────────────────────────
const KPI_CONFIG = [
  { label: 'Total Students',    key: 'total',                 icon: '🎓', color: '#3249D7', bg: '#EEF2FF', filterKey: null,              filterValue: null  },
  { label: 'Status: Active',    key: 'status_active',         icon: '🟢', color: '#10B981', bg: '#D1FAE5', filterKey: 'status',           filterValue: '2'     },
  { label: 'Status: Inactive',  key: 'status_inactive',       icon: '🔴', color: '#EF4444', bg: '#FEE2E2', filterKey: 'status',           filterValue: '-2'    },
  { label: 'Status: Pending',   key: 'status_pending',        icon: '🟡', color: '#F59E0B', bg: '#FEF3C7', filterKey: 'status',           filterValue: '1'     },
  { label: 'Blacklisted',       key: 'status_blacklisted',    icon: '🚫', color: '#64748b', bg: '#F1F5F9', filterKey: 'status',           filterValue: '-1'    },
  { label: 'Not Interested',    key: 'status_not_interested', icon: '😶', color: '#EC4899', bg: '#FCE7F3', filterKey: 'status',           filterValue: '-3'    },
  { label: 'Invite Sent',           key: 'status_unknown',        icon: '⭕', color: '#94a3b8', bg: '#F8FAFC', filterKey: 'status',           filterValue: '0'     },
  { label: 'Passed Out',        key: 'passed_out',            icon: '🏫', color: '#F59E0B', bg: '#FEF3C7', filterKey: 'passed_out',       filterValue: 'true'  },
  { label: 'Exp Candidates',    key: 'exp_candidates',        icon: '💼', color: '#8B5CF6', bg: '#EDE9FE', filterKey: 'is_exp_candidate', filterValue: 'true'  },
  { label: 'Profile > 75%',      key: 'profile_complete',      icon: '⭐', color: '#EF4444', bg: '#FEE2E2', filterKey: 'profile_min',      filterValue: '76', filterExtra: { profile_max: '100' } },
  { label: 'Avg Profile %',     key: 'avg_profile_perc',      icon: '📊', color: '#06B6D4', bg: '#CFFAFE', filterKey: 'profile_min', filterValue: '1', filterExtra: { profile_max: '100' }, suffix: '%' },
  
]

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  typeof n === 'number' ? String(n) : n

const toProperCase = (str) => {
  if (!str) return str
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase())
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{
      background: '#1e293b', border: '1px solid #334155',
      borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#f1f5f9',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#94a3b8' }}>
          {p.name}: <b>{p.value?.toLocaleString()}</b>
        </div>
      ))}
    </div>
  )
}

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null
  const RADIAN = Math.PI / 180
  const r = innerRadius + (outerRadius - innerRadius) * 0.55
  const x = cx + r * Math.cos(-midAngle * RADIAN)
  const y = cy + r * Math.sin(-midAngle * RADIAN)
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  )
}

const GenderLegend = ({ data }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '6px 16px', marginTop: 12,
  }}>
    {data.map((entry, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: PIE_COLORS[entry.label] || PALETTE[i % PALETTE.length],
        }} />
        <span style={{ color: '#475569', flex: 1 }}>{entry.label}</span>
        <span style={{ fontWeight: 700, color: '#1e293b' }}>{entry.count.toLocaleString()}</span>
        <span style={{ color: '#94a3b8', fontSize: 11 }}>
          {`(${((entry.count / data.reduce((s, d) => s + d.count, 0)) * 100).toFixed(1)}%)`}
        </span>
      </div>
    ))}
  </div>
)

// ── View-Only Tooltip ─────────────────────────────────────────────────────────
// Follows the mouse and shows "View only" when readOnly is active
const ViewOnlyTooltip = () => {
  const ref = useRef(null)

  useEffect(() => {
    const move = (e) => {
      if (ref.current) {
        ref.current.style.left = `${e.clientX + 14}px`
        ref.current.style.top  = `${e.clientY - 10}px`
      }
    }
    window.addEventListener('mousemove', move)
    return () => window.removeEventListener('mousemove', move)
  }, [])

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 9999,
        background: '#1e293b',
        color: '#f1f5f9',
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 10px',
        borderRadius: 6,
        letterSpacing: '0.02em',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
        transition: 'opacity 0.1s',
      }}
    >
      View only
    </div>
  )
}

// ── ReadOnly Overlay ──────────────────────────────────────────────────────────
// Sits over any interactive element.
// pointerEvents: 'none' lets hover/tooltip events pass through to the chart,
// but we intercept clicks via onClickCapture to swallow navigation.
const ReadOnlyOverlay = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 10,
      cursor: 'not-allowed',
      borderRadius: 'inherit',
      pointerEvents: 'none', // allows hover tooltips through
    }}
  />
)

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ title, children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: 14,
    border: '1px solid #e8ecf4', padding: '20px 24px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', ...style,
  }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 18 }}>
      {title}
    </div>
    {children}
  </div>
)

const NoData = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 200, color: '#94a3b8', fontSize: 13, fontWeight: 500,
  }}>
    No Data
  </div>
)

const makeLeftTick = (maxChars = 18) => ({ x, y, payload }) => {
  const proper = toProperCase(payload.value)
  const text = proper.length > maxChars ? `${proper.slice(0, maxChars)}…` : proper
  return (
    <g transform={`translate(0,${y})`}>
      {/* <text x={4} y={0} dy={4} textAnchor="start" fontSize={11} fill="#64748b" > */}
      <text x={4} y={0} dy={4} textAnchor="start" fontSize={11} fill="#64748b" style={{ cursor: 'default' }}>
        <title>{proper}</title>
        {text}
        </text>
    </g>
  )
}

const makeBottomTick = (maxChars = 10) => ({ x, y, payload }) => {
  const proper = toProperCase(payload.value)
  const text = proper.length > maxChars ? `${proper.slice(0, maxChars)}…` : proper
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0} y={0} dy={10}
        textAnchor="end"
        fontSize={11}
        fill="#64748b"
        transform="rotate(-35)"
      >
        <title>{proper}</title>
        {text}
      </text>
    </g>
  )
}

// shorthand alias — dashboard slice of the shared analytics session
const _dash = () => analyticsSession.dashboard

const DEFAULT_CANDIDATE_FILTERS = {
  gender: null, status: null, passed_out: null, profile: [0, 100],
  institute_campus: [], institute_campus_id: null, institute_campus_ids: [],
  education_level: null, degree: [], department: [], city: [], state: [],
  is_exp_candidate: null, roles: [], work_company: [], work_designation: [],
  work_industry: [], work_location: '', work_started_from: null, work_started_to: null,
  notice_period: null, work_corporate_ids: [], salary_min: '', salary_max: '', passing_years: [],
}

const isClickInsidePlot = (event, margin) => {
  const svg = event?.nativeEvent?.target?.closest('svg')
  if (!svg) return false
  const rect = svg.getBoundingClientRect()
  const x = event.nativeEvent.clientX - rect.left
  const y = event.nativeEvent.clientY - rect.top
  const plotLeft   = (margin.left ?? 0) + 30     // YAxis width ~30px
  const plotRight  = rect.width - (margin.right ?? 0)
  const plotTop    = margin.top ?? 0
  const plotBottom = rect.height - (margin.bottom ?? 0) - 20 // XAxis ~20px
  return x >= plotLeft && x <= plotRight && y >= plotTop && y <= plotBottom
}

// ── Main Component ─────────────────────────────────────────────────────────────
const CandidateDashboard = ({ readOnly = false }) => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [data, setData]       = useState(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const hoveredLabel = useRef(null)

  const [filters, setFilters] = useState(() => ({ ..._dash() }))

  const updateFilter = (patch) => {
    Object.assign(analyticsSession.dashboard, patch)
    setFilters(prev => ({ ...prev, ...patch }))
  }

  const resetInstituteFilter = () => {
    analyticsSession.dashboard.institute_campus_id = null
    analyticsSession.dashboard.institute_campus_name = null
    analyticsSession.dashboard.institute_campus_option = null
    if (analyticsSession.candidates.filters) {
      analyticsSession.candidates.filters.institute_campus_ids = []
    }
    setFilters(prev => ({ ...prev, institute_campus_id: null, institute_campus_name: null }))
  }

  const resetCorporateFilter = () => {
    analyticsSession.dashboard.corporate_id = null
    analyticsSession.dashboard.corporate_name = null
    analyticsSession.dashboard.corporate_option = null
    if (analyticsSession.candidates.filters) {
      analyticsSession.candidates.filters.work_corporate_ids = []
    }
    setFilters(prev => ({ ...prev, corporate_id: null, corporate_name: null }))
  }

  // Reset shared session when navigating away from analytics pages
  useEffect(() => () => resetSessionIfLeavingAnalytics(), [])

  const [instituteOptions, setInstituteOptions]   = useState(
    () => analyticsSession.dashboard.institute_campus_option ? [analyticsSession.dashboard.institute_campus_option] : []
  )
  const [institutePage, setInstitutePage]         = useState(1)
  const [instituteHasMore, setInstituteHasMore]   = useState(true)
  const [instituteSearch, setInstituteSearch]     = useState('')
  const [instituteLoading, setInstituteLoading]   = useState(false)
  const instituteSearchTimer                      = useRef(null)

  const [corporateOptions, setCorporateOptions]   = useState(
    () => analyticsSession.dashboard.corporate_option ? [analyticsSession.dashboard.corporate_option] : []
  )
  const [corporatePage, setCorporatePage]         = useState(1)
  const [corporateHasMore, setCorporateHasMore]   = useState(true)
  const [corporateSearch, setCorporateSearch]     = useState('')
  const [corporateLoading, setCorporateLoading]   = useState(false)
  const corporateSearchTimer                      = useRef(null)

  const fetchInstitutes = useCallback(async (page = 1, search = '', append = false) => {
    setInstituteLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/master-institutes', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r.id, label: r.name }))
      setInstituteOptions(prev => append ? [...prev, ...items] : items)
      setInstituteHasMore(items.length === 10)
      setInstitutePage(page)
    } catch { /* silent */ } finally {
      setInstituteLoading(false)
    }
  }, [])

  const fetchCorporates = useCallback(async (page = 1, search = '', append = false) => {
    setCorporateLoading(true)
    try {
      const resp = await candidateRequest.get('/api/student-metrics/master-corporates', {
        params: { page, limit: 10, search: search || undefined },
      })
      const items = (resp?.data?.data || []).map(r => ({ value: r.id, label: r.name }))
      setCorporateOptions(prev => append ? [...prev, ...items] : items)
      setCorporateHasMore(items.length === 10)
      setCorporatePage(page)
    } catch { /* silent */ } finally {
      setCorporateLoading(false)
    }
  }, [])

  const fetchSummary = useCallback(async (activeFilters) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (activeFilters?.institute_campus_id) params.append('institute_campus_id', activeFilters.institute_campus_id)
      if (activeFilters?.corporate_id) params.append('corporate_id', activeFilters.corporate_id)
      const qs = params.toString()
      const res = await candidateRequest.get(`/api/student-metrics/dashboard-summary${qs ? `?${qs}` : ''}`)
      setData(res?.data?.data || null)
    } catch {
      message.error('Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSummary(filters) }, [fetchSummary, filters])

  // ── Navigate (no-op in readOnly) ──────────────────────────────────────────
  // Pass active dashboard filters by NAME to the list page (filter panel uses names)
  const goToList = (params = {}) => {
    if (readOnly) return
    const parsed = { ...DEFAULT_CANDIDATE_FILTERS }
    if (params.status !== undefined)             parsed.status = parseInt(params.status, 10)
    if (params.gender)                           parsed.gender = params.gender
    if (params.passed_out !== undefined)         parsed.passed_out = params.passed_out === 'true' ? true : params.passed_out === 'false' ? false : null
    if (params.is_exp_candidate !== undefined)   parsed.is_exp_candidate = params.is_exp_candidate === 'true' ? true : params.is_exp_candidate === 'false' ? false : null
    if (params.education_level)                  parsed.education_level = params.education_level
    if (params.degree)                           parsed.degree = [params.degree]
    if (params.department)                       parsed.department = [params.department]
    if (params.city)                             parsed.city = [params.city]
    if (params.state)                            parsed.state = [params.state]
    if (params.roles)                            parsed.roles = [params.roles]
    if (params.institute_campus)                 parsed.institute_campus = [params.institute_campus]
    if (params.profile_min !== undefined || params.profile_max !== undefined)
      parsed.profile = [params.profile_min ?? 0, params.profile_max ?? 100]
    if (filters.institute_campus_id)             parsed.institute_campus_ids = [filters.institute_campus_id]
    if (filters.corporate_id)                    parsed.work_corporate_ids = [filters.corporate_id]
    analyticsSession.candidates = { filters: parsed, searchValue: '', openTab: 'main' }
    navigate('/dashboards/candidate-metric-details')
  }

  const onBarClick = (filterKey, transform) => (chartData) => {
    if (readOnly || !filterKey || !chartData?.activePayload?.[0]) return
    const raw = chartData.activePayload[0].payload.label
    goToList({ [filterKey]: transform ? transform(raw) : raw, status: 2 })
  }

  const profileBucketParams = (label) => {
    const map = {
      '0–25%':  { profile_min: 0,   profile_max: 25  },
      '26–50%': { profile_min: 26,  profile_max: 50  },
      '51–75%': { profile_min: 51,  profile_max: 75  },
      '76–99%': { profile_min: 76,  profile_max: 99  },
      '100%':   { profile_min: 100, profile_max: 100 },
    }
    return map[label] || {}
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
      <Spin size="large" />
    </div>
  )

  if (!data) return <Empty description="No dashboard data" />

  const { kpis } = data

  // ── Shared readOnly mouse handlers for the wrapper ────────────────────────
  const roProps = readOnly
    ? {
        onMouseEnter: () => setShowTooltip(true),
        onMouseLeave: () => setShowTooltip(false),
      }
    : {}

  return (
    <div
      style={{
        background: '#f4f6fb', minHeight: '100vh',
        padding: '24px 28px', fontFamily: "'DM Sans', sans-serif",
        userSelect: 'none',
        ...(readOnly ? {} : {}),
      }}
      {...roProps}
    >
      {/* ── View-only floating tooltip ──────────────────────────────────── */}
      {readOnly && showTooltip && <ViewOnlyTooltip />}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, gap: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
            Candidate Analytics
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 14 }}>
            Live overview of your student database
          </div>
        </div>

        {/* ── Filter Bar ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <Select
            allowClear
            showSearch
            style={{ width: 260 }}
            placeholder="Filter by Institute"
            loading={instituteLoading}
            options={instituteOptions}
            value={filters.institute_campus_id}
            onChange={(val, option) => {
              const opt = val ? { value: option?.value, label: option?.label } : null
              analyticsSession.dashboard.institute_campus_option = opt
              updateFilter({
                institute_campus_id: val ?? null,
                institute_campus_name: val ? option?.label : null,
              })
            }}
            onDropdownVisibleChange={(open) => {
              if (open) {
                setInstituteSearch('')
                setInstitutePage(1)
                setInstituteHasMore(true)
                fetchInstitutes(1, '')
              }
            }}
            onSearch={(v) => {
              clearTimeout(instituteSearchTimer.current)
              if (v.length < 3) return
              instituteSearchTimer.current = setTimeout(() => {
                setInstituteSearch(v)
                fetchInstitutes(1, v)
              }, 400)
            }}
            onPopupScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target
              if (scrollHeight - scrollTop <= clientHeight + 20 && instituteHasMore && !instituteLoading)
                fetchInstitutes(institutePage + 1, instituteSearch, true)
            }}
            onClear={() => {
              resetInstituteFilter()
              setInstituteSearch('')
              setInstituteOptions([])
              setInstitutePage(1)
              setInstituteHasMore(true)
            }}
            filterOption={false}
            notFoundContent={instituteLoading ? <Spin size="small" /> : 'No institutes found'}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            placeholder="Filter by Corporate"
            loading={corporateLoading}
            options={corporateOptions}
            value={filters.corporate_id}
            onChange={(val, option) => {
              const opt = val ? { value: option?.value, label: option?.label } : null
              analyticsSession.dashboard.corporate_option = opt
              updateFilter({
                corporate_id: val ?? null,
                corporate_name: val ? option?.label : null,
              })
            }}
            onDropdownVisibleChange={(open) => {
              if (open) {
                setCorporateSearch('')
                setCorporatePage(1)
                setCorporateHasMore(true)
                fetchCorporates(1, '')
              }
            }}
            onSearch={(v) => {
              clearTimeout(corporateSearchTimer.current)
              if (v.length < 3) return
              corporateSearchTimer.current = setTimeout(() => {
                setCorporateSearch(v)
                fetchCorporates(1, v)
              }, 400)
            }}
            onPopupScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.target
              if (scrollHeight - scrollTop <= clientHeight + 20 && corporateHasMore && !corporateLoading)
                fetchCorporates(corporatePage + 1, corporateSearch, true)
            }}
            onClear={() => {
              resetCorporateFilter()
              setCorporateSearch('')
              setCorporateOptions([])
              setCorporatePage(1)
              setCorporateHasMore(true)
            }}
            filterOption={false}
            notFoundContent={corporateLoading ? <Spin size="small" /> : 'No corporates found'}
          />

          {(() => {
            const hasAny = !!(filters.institute_campus_id || filters.corporate_id)
            return (
              <button
                disabled={!hasAny}
                onClick={() => {
                  if (!hasAny) return
                  resetInstituteFilter()
                  resetCorporateFilter()
                  analyticsSession.candidates = { filters: null, searchValue: '' }
                  setInstituteSearch(''); setInstituteOptions([]); setInstitutePage(1); setInstituteHasMore(true)
                  setCorporateSearch(''); setCorporateOptions([]); setCorporatePage(1); setCorporateHasMore(true)
                }}
                style={{
                  background: hasAny ? '#3249D7' : '#f1f5f9',
                  border: `1px solid ${hasAny ? '#3249D7' : '#e2e8f0'}`,
                  borderRadius: 8,
                  padding: '5px 16px',
                  cursor: hasAny ? 'pointer' : 'not-allowed',
                  fontSize: 12,
                  color: hasAny ? '#fff' : '#94a3b8',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                  flexShrink: 0,
                }}
              >
                Reset
              </button>
            )
          })()}
        </div> {/* end filter bar */}
      </div> {/* end header */}

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: 16, marginBottom: 24,
      }}>
        {KPI_CONFIG.map((cfg) => {
          const { label, key, icon, color, bg, filterKey, filterValue, suffix } = cfg
          const isClickable = !readOnly
          return (
            <div
              key={key}
              onClick={() => {
                if (readOnly) return
                goToList(cfg.filterKey ? { [cfg.filterKey]: filterValue, ...(cfg.filterExtra || {}) } : {})
              }}
              style={{
                background: '#fff', borderRadius: 14, padding: '18px 20px',
                border: '1px solid #e8ecf4',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                cursor: readOnly ? 'not-allowed' : 'pointer',
                transition: 'all 0.18s',
                position: 'relative', overflow: 'hidden',
              }}
              onMouseEnter={e => {
                if (!readOnly) {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(50,73,215,0.12)'
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'none'
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'
              }}
            >
              <div style={{
                position: 'absolute', top: 0, right: 0,
                width: 80, height: 80, background: bg,
                borderRadius: '0 14px 0 80px', opacity: 0.6,
              }} />
              <div style={{
                width: 38, height: 38, background: bg,
                borderRadius: 10, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 18, marginBottom: 12,
              }}>
                {icon}
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.5px' }}>
                {key === 'avg_profile_perc'
                  ? `${kpis[key]?.toFixed(1)}${suffix || ''}`
                  : fmt(kpis[key])}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 5, fontWeight: 500 }}>
                {label}
              </div>
              {!readOnly && (
                <div style={{ fontSize: 10, color, marginTop: 6, fontWeight: 600, opacity: 0.8 }}>
                  {!cfg.filterKey ? 'View all candidates →' : 'View all →'}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* ── Row 1: 3 Donuts ────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>

        {/* Gender */}
        <Section title="👥 Gender Distribution">
          {!data.gender_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.gender_dist} dataKey="count" nameKey="label"
                    cx="50%" cy="50%" innerRadius={55} outerRadius={110}
                    labelLine={false} label={CustomPieLabel}
                    onClick={readOnly ? undefined : (entry) => goToList({ gender: entry.raw_gender || entry.label?.toUpperCase(), status: 2 })}
                    style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  >
                    {data.gender_dist.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[entry.label] || PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val, name) => [val.toLocaleString(), name]} />
                </PieChart>
              </ResponsiveContainer>
              <GenderLegend data={data.gender_dist} />
            </div>
          )}
        </Section>

        {/* Passed Out */}
        <Section title="🎓 Active - Passed Out">
          {!data.passed_out_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={300}> 
                <PieChart>
                  <Pie
                    data={data.passed_out_dist} dataKey="count" nameKey="label"
                    cx="50%" cy="50%" innerRadius={55} outerRadius={110}
                    labelLine={false} label={CustomPieLabel}
                    onClick={readOnly ? undefined : (entry) => {
                      const val = entry.label === 'Passed Out' ? 'true' : 'false'
                      goToList({ passed_out: val, status: 2 })
                    }}
                    style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  >
                    {data.passed_out_dist.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[entry.label] || PALETTE[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => [val.toLocaleString()]} />
                  <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        {/* Exp vs Fresher */}
        <Section title="💼 Experience Type">
          {!data.exp_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.exp_dist} dataKey="count" nameKey="label"
                    cx="50%" cy="50%" innerRadius={55} outerRadius={110}
                    labelLine={false} label={CustomPieLabel}
                    onClick={readOnly ? undefined : (entry) => {
                      const val = entry.label === 'Experienced' ? 'true' : 'false'
                      goToList({ is_exp_candidate: val, status: 2 })
                    }}
                    style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  >
                    {data.exp_dist.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[entry.label] || PALETTE[i]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => [val.toLocaleString()]} />
                  <Legend iconType="circle" iconSize={9} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>

      {/* ── Row 2: Education Level + Profile Buckets ────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        <Section title="📚 Highest Education Level">
          {!data.education_dist?.length ? <NoData /> : (
            <div
              style={{ position: 'relative' }}
              onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}
              onMouseLeave={() => { isInsideEducation.current = false }}
            >
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={data.education_dist}
                  margin={{ top: 4, right: 12, left: -10, bottom: 40 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onMouseMove={(chartData) => {
                    isInsideEducation.current = !!chartData?.activeLabel
                  }}
                  onMouseLeave={() => { isInsideEducation.current = false }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    if (!isClickInsidePlot(event, { left: -10, right: 12, top: 4, bottom: 40 })) return
                    const label = chartData?.activeLabel
                    if (!label) return
                    const payload = data.education_dist.find(d => d.label === label)
                    if (!payload) return
                    goToList({ education_level: payload.label.toLowerCase(), status: 2 })
                  }}
                >
            
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      angle={-35}
                      textAnchor="end"
                      interval={0}
                      tickFormatter={(v) => {
                        const proper = toProperCase(v)
                        return proper.length > 40 ? `${proper.slice(0, 40)}…` : proper
                      }}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="count" name="Students" radius={[6, 6, 0, 0]} fill={BRAND}
                      cursor={readOnly ? 'not-allowed' : 'pointer'}
                      onClick={readOnly ? undefined : (barData) => goToList({ education_level: barData.label.toLowerCase(), status: 2 })}
                    >
                      {data.education_dist.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        <Section title="📈 Profile Completion Buckets">
          {!data.profile_buckets?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={data.profile_buckets}
                  margin={{ top: 4, right: 12, left: -10, bottom: 0 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onMouseMove={(chartData) => {
                    isInsideChart.current = !!chartData?.activeLabel
                  }}
                  onMouseLeave={() => { isInsideChart.current = false }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    if (!isClickInsidePlot(event, { left: -10, right: 12, top: 4, bottom: 0 })) return
                    const label = chartData?.activeLabel
                    if (!label) return
                    const payload = data.profile_buckets.find(d => d.label === label)
                    if (!payload) return
                    goToList({ ...profileBucketParams(payload.label), status: 2 })
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={(v) => toProperCase(v)}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[6, 6, 0, 0]} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ ...profileBucketParams(barData.label), status: 2 })}>
                    {data.profile_buckets.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>

      {/* ── Row 3: Top Roles + Top Degrees ─────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        <Section title="🎯 Top 10 Roles Applied">
          {!data.roles_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  layout="vertical" data={data.roles_dist}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    const x = event?.nativeEvent?.offsetX ?? 0
                    if (x < 134) return
                    const payload = chartData?.activePayload?.[0]?.payload
                      ?? data.roles_dist.find(d => d.label === chartData?.activeLabel)
                    if (!payload) return
                    goToList({ roles: payload.label, status: 2 })
                  }}
                  // onMouseMove={(s) => { hoveredLabel.current = s?.activePayload?.[0]?.payload?.label ?? s?.activeLabel ?? null }}
                  // onMouseLeave={() => { hoveredLabel.current = null }}
                  // onClick={readOnly ? undefined : () => { if (hoveredLabel.current) goToList({ roles: hoveredLabel.current, status: 2 }) }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="label" width={130} tick={makeLeftTick()} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[0, 6, 6, 0]} fill={BRAND} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ roles: barData.label, status: 2 })}>
                    {data.roles_dist.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        <Section title="🎓 Top 10 Degrees">
          {!data.degree_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  layout="vertical" data={data.degree_dist}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    const x = event?.nativeEvent?.offsetX ?? 0
                    if (x < 134) return
                    const payload = chartData?.activePayload?.[0]?.payload
                      ?? data.degree_dist.find(d => d.label === chartData?.activeLabel)
                    if (!payload) return
                    goToList({ degree: payload.label, status: 2 })
                  }}
                  // onMouseMove={(s) => { hoveredLabel.current = s?.activePayload?.[0]?.payload?.label ?? s?.activeLabel ?? null }}
                  // onMouseLeave={() => { hoveredLabel.current = null }}
                  // onClick={readOnly ? undefined : () => { if (hoveredLabel.current) goToList({ degree: hoveredLabel.current, status: 2 }) }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="label" width={130} tick={makeLeftTick()} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[0, 6, 6, 0]} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ degree: barData.label, status: 2 })}>
                    {data.degree_dist.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>

      {/* ── Row 4: Institute + Department ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        <Section title="🏫 Top 10 Institutes">
          {!data.institute_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  layout="vertical" data={data.institute_dist}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    const x = event?.nativeEvent?.offsetX ?? 0
                    if (x < 134) return
                    const payload = chartData?.activePayload?.[0]?.payload
                      ?? data.institute_dist.find(d => d.label === chartData?.activeLabel)
                    if (!payload) return
                    goToList({ institute_campus: payload.label, status: 2 })
                  }}
                  // onMouseMove={(s) => { hoveredLabel.current = s?.activePayload?.[0]?.payload?.label ?? s?.activeLabel ?? null }}
                  // onMouseLeave={() => { hoveredLabel.current = null }}
                  // onClick={readOnly ? undefined : () => { if (hoveredLabel.current) goToList({ institute_campus: hoveredLabel.current, status: 2 }) }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="label" width={130} tick={makeLeftTick()} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[0, 6, 6, 0]} fill={BRAND} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ institute_campus: barData.label, status: 2 })}>
                    {data.institute_dist.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        <Section title="🔬 Top 10 Departments">
          {!data.department_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  layout="vertical" data={data.department_dist}
                  margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    const x = event?.nativeEvent?.offsetX ?? 0
                    if (x < 134) return
                    const payload = chartData?.activePayload?.[0]?.payload
                      ?? data.department_dist.find(d => d.label === chartData?.activeLabel)
                    if (!payload) return
                    goToList({ department: payload.label, status: 2 })
                  }}
                  // onMouseMove={(s) => { hoveredLabel.current = s?.activePayload?.[0]?.payload?.label ?? s?.activeLabel ?? null }}
                  // onMouseLeave={() => { hoveredLabel.current = null }}
                  // onClick={readOnly ? undefined : () => { if (hoveredLabel.current) goToList({ department: hoveredLabel.current, status: 2 }) }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis type="category" dataKey="label" width={130} tick={makeLeftTick()} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[0, 6, 6, 0]} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ department: barData.label, status: 2 })}>
                    {data.department_dist.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>

      {/* ── Row 5: City + State ─────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

        <Section title="📍 Top 10 Cities">
          {!data.city_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={data.city_dist}
                  margin={{ top: 4, right: 12, left: -10, bottom: 90 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onMouseMove={(chartData) => {
                    isInsideChart.current = !!chartData?.activeLabel
                  }}
                  onMouseLeave={() => { isInsideChart.current = false }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    if (!isClickInsidePlot(event, { left: -10, right: 12, top: 4, bottom: 90 })) return
                    const label = chartData?.activeLabel
                    if (!label) return
                    const payload = data.city_dist.find(d => d.label === label)
                    if (!payload) return
                    goToList({ city: payload.label, status: 2 })
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" interval={0} tick={makeBottomTick()} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[6, 6, 0, 0]} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ city: barData.label, status: 2 })}>
                    {data.city_dist.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>

        <Section title="🗺️ Top 10 States">
          {!data.state_dist?.length ? <NoData /> : (
            <div style={{ position: 'relative' }} onClickCapture={readOnly ? (e) => e.stopPropagation() : undefined}>
              {readOnly && <ReadOnlyOverlay />}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={data.state_dist}
                  margin={{ top: 4, right: 12, left: -10, bottom: 90 }}
                  style={{ cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  onMouseMove={(chartData) => {
                    isInsideChart.current = !!chartData?.activeLabel
                  }}
                  onMouseLeave={() => { isInsideChart.current = false }}
                  onClick={readOnly ? undefined : (chartData, event) => {
                    if (!isClickInsidePlot(event, { left: -10, right: 12, top: 4, bottom: 90 })) return
                    const label = chartData?.activeLabel
                    if (!label) return
                    const payload = data.state_dist.find(d => d.label === label)
                    if (!payload) return
                    goToList({ state: payload.label, status: 2 })
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" interval={0} tick={makeBottomTick()} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Students" radius={[6, 6, 0, 0]} cursor={readOnly ? 'not-allowed' : 'pointer'}
                    onClick={readOnly ? undefined : (barData) => goToList({ state: barData.label, status: 2 })}>
                    {data.state_dist.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>

    </div>
  )
}

export default CandidateDashboard